"use client";
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const accordionItemVariants = cva(
  'border rounded-lg bg-white transition-all duration-200',
  {
    variants: {
      variant: {
        default: 'border-neutral-200 shadow-sm hover:shadow-md',
        elevated: 'border-neutral-200 shadow-md hover:shadow-lg',
        outlined: 'border-2 border-neutral-300 shadow-none hover:border-[color:var(--color-primary)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export function Accordion({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('space-y-3', className)}>{children}</div>;
}

interface AccordionItemProps extends VariantProps<typeof accordionItemVariants> {
  title: string;
  children: React.ReactNode;
}

export function AccordionItem({ title, children, variant }: AccordionItemProps) {
  const [open, setOpen] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (open && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [open]);

  return (
    <div className={cn(accordionItemVariants({ variant }))}>
      <button
        type="button"
        className={cn(
          'w-full text-left p-5 font-semibold flex items-center justify-between',
          'text-[color:var(--color-text-primary)] hover:text-[color:var(--color-primary)]',
          'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 rounded-lg'
        )}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="text-base md:text-lg">{title}</span>
        <span 
          aria-hidden 
          className={cn(
            'text-2xl font-bold transition-transform duration-300 text-[color:var(--color-primary)]',
            open && 'rotate-45'
          )}
        >
          +
        </span>
      </button>
      <div
        style={{
          height: height,
          overflow: 'hidden',
          transition: 'height 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div ref={contentRef} className="p-5 pt-0 border-t border-neutral-100 text-[color:var(--color-text-secondary)]">
          {children}
        </div>
      </div>
    </div>
  );
}
